import { useState } from "react";
import { useApi } from "../hooks/useApi";
import { theme, fonts } from "../theme";
import { Card, CardHeader, StatusBadge, Pill, SectionTitle, Metric, Button, AccentStripe, ErrorState, formatPnl, pnlColor } from "../components/ui";
import MarketDataWidget from "../components/MarketDataWidget";
import EquityChart, { BarChart } from "../components/EquityChart";
import { SkeletonCards } from "../components/shared";
import { API_BASE } from "../config";

export default function Dashboard({ navigate }) {
  const { data: status, error, loading } = useApi("/status", 3000);
  const { data: accounts } = useApi("/accounts", 10000);
  const { data: trades } = useApi("/trades?limit=500", 3000);
  const { data: equityHistory } = useApi("/equity/history", 30000);
  const { data: recon } = useApi("/reconciliation/status", 10000);
  // v4-FEAT-E: Latest analyze runs for tile
  const { data: analyzeRuns } = useApi("/analyze/runs", 60000);
  const [showTest, setShowTest] = useState(false);

  if (loading) return <SkeletonCards n={6} />;
  if (error) return <ErrorState error={String(error)} />;
  if (!status) return null;

  const nav = navigate || (() => {});
  const strategies = status.strategies || {};
  const positions = status.positions || {};
  const adapters = status.adapters || {};
  const ibConnected = Object.values(adapters).some(a => a.connected);
  const equity = (Object.values(adapters).find(a => a.connected) || {}).equity || 0;
  const allTrades = trades?.trades || [];
  const acctList = accounts?.accounts || [];
  const hasMismatch = recon?.has_mismatches;

  const exits = allTrades.filter(t => t.action === "exit");
  const totalPnl = exits.reduce((s, t) => s + (t.pnl || 0), 0);
  const wins = exits.filter(t => (t.pnl || 0) > 0);
  const winRate = exits.length > 0 ? ((wins.length / exits.length) * 100).toFixed(1) : "0.0";
  const todayStr = new Date().toISOString().slice(0, 10);
  const todayTrades = exits.filter(t => t.timestamp && t.timestamp.startsWith(todayStr));
  const todayPnl = todayTrades.reduce((s, t) => s + (t.pnl || 0), 0);

  // Equity chart data: prefer API snapshots, fallback to trade-derived curve
  const eqFromApi = equityHistory?.history?.map(h => h.equity) || [];
  let eqData = eqFromApi;
  if (eqFromApi.length < 5 && exits.length > 0) {
    const sorted = [...exits].sort((a, b) => String(a.timestamp || "").localeCompare(String(b.timestamp || "")));
    const start = equity - totalPnl;
    eqData = [start];
    let r = start;
    sorted.forEach(t => { r += (t.pnl || 0); eqData.push(r); });
  }

  // Hourly P&L
  const hourlyMap = {};
  todayTrades.forEach(t => { if (t.timestamp) { const h = new Date(t.timestamp).getHours(); hourlyMap[h] = (hourlyMap[h] || 0) + (t.pnl || 0); } });
  const hours = Array.from({ length: 12 }, (_, i) => i + 8);
  const hourlyData = hours.map(h => hourlyMap[h] || 0);
  const hourlyLabels = hours.map(h => String(h).padStart(2, "0"));

  // Categorize strategies
  const buildBot = (name, strat) => {
    const st = strat || {};
    const stratTrades = exits.filter(t => t.strategy === name);
    const sWins = stratTrades.filter(t => (t.pnl || 0) > 0);
    const pnl = stratTrades.reduce((s, t) => s + (t.pnl || 0), 0);
    const wr = stratTrades.length > 0 ? ((sWins.length / stratTrades.length) * 100).toFixed(1) : "0.0";
    const tToday = stratTrades.filter(t => t.timestamp && t.timestamp.startsWith(todayStr));
    return { name, strat: st, pnl, wr, trades: stratTrades.length, todayPnl: tToday.reduce((s, t) => s + (t.pnl || 0), 0), todayTrades: tToday.length, pos: positions[name], isPositive: pnl >= 0 };
  };

  // Status-Modell (Sprint 1.4): test / paper / live
  const liveBots = Object.entries(strategies).filter(([, s]) => s.status === "live" && s.adapter_connected).map(([n, s]) => buildBot(n, s));
  const warningBots = Object.entries(strategies).filter(([, s]) => s.status === "live" && !s.adapter_connected).map(([n, s]) => buildBot(n, s));
  const paperBots = Object.entries(strategies).filter(([, s]) => s.status === "paper" && s.adapter_connected).map(([n, s]) => buildBot(n, s));
  const paperWarningBots = Object.entries(strategies).filter(([, s]) => s.status === "paper" && !s.adapter_connected).map(([n, s]) => buildBot(n, s));
  const testBots = Object.entries(strategies).filter(([, s]) => s.status !== "live" && s.status !== "paper" && s.status !== "paused").map(([n, s]) => buildBot(n, s));
  const recentTrades = allTrades.slice(0, 15);

  const BotCard = ({ bot }) => {
    // v8.0: Profile-Color-Map
    const profileColors = {
      KONSERVATIV: { bg: "rgba(34,197,94,0.10)", text: "#16a34a", border: "rgba(34,197,94,0.3)" },
      MODERAT:     { bg: "rgba(59,130,246,0.10)", text: "#3b82f6", border: "rgba(59,130,246,0.3)" },
      AGGRESSIV:   { bg: "rgba(239,68,68,0.10)",  text: "#ef4444", border: "rgba(239,68,68,0.3)" },
    };
    const profile = bot.strat.profile;
    const pColor = profile && profileColors[profile];
    
    // Position-Size-Tracking
    const currentQty = bot.pos ? Number(bot.pos.qty || bot.pos.quantity || 0) : 0;
    const maxContracts = Number(bot.strat.max_contracts || 0);
    const capUsage = maxContracts > 0 && currentQty > 0
      ? Math.round((currentQty / maxContracts) * 100)
      : 0;
    
    return (
    <Card className="sl-card">
      <AccentStripe positive={bot.isPositive} />
      <div style={{ padding: "16px 20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div style={{ width: 42, height: 42, borderRadius: 12, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: bot.isPositive ? theme.greenLight : theme.redLight, border: `1px solid ${bot.isPositive ? "rgba(22,163,74,0.1)" : "rgba(220,38,38,0.08)"}` }}>
              <span style={{ fontFamily: fonts.mono, fontWeight: 800, fontSize: 11, color: bot.isPositive ? theme.greenText : theme.redText }}>{String(bot.strat.symbol || bot.name.split("_")[0])}</span>
              <span style={{ fontFamily: fonts.mono, fontSize: 8, color: bot.isPositive ? theme.green : theme.red, fontWeight: 600 }}>{String(bot.strat.timeframe || "M5")}</span>
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>{String(bot.name)}</div>
              <div style={{ fontSize: 11, color: theme.text3, fontFamily: fonts.mono, display: "flex", alignItems: "center", gap: 6 }}>
                <span>{String(bot.strat.exchange || "IBKR")}</span>
                <span>{"\u00B7"}</span>
                <span>{String(bot.strat.account || "Paper")}</span>
                {profile && pColor && (
                  <span style={{
                    marginLeft: 4,
                    padding: "1px 7px",
                    borderRadius: 6,
                    fontSize: 9,
                    fontWeight: 800,
                    letterSpacing: "0.5px",
                    background: pColor.bg,
                    color: pColor.text,
                    border: `1px solid ${pColor.border}`,
                  }}>{profile}</span>
                )}
              </div>
            </div>
          </div>
          <StatusBadge status={String(bot.strat.status || "test")} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 4 }}>Total P&L</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ fontSize: 26, fontWeight: 900, fontFamily: fonts.mono, color: pnlColor(bot.pnl), letterSpacing: "-1px" }}>{formatPnl(bot.pnl)}</span>
            <span style={{ fontSize: 11, fontWeight: 600, fontFamily: fonts.mono, color: pnlColor(bot.todayPnl), padding: "2px 7px", borderRadius: 5, background: bot.todayPnl >= 0 ? theme.greenLight : theme.redLight }}>{formatPnl(bot.todayPnl)} today</span>
          </div>
        </div>
        
        {/* v8.0: Risk-Profile-Info-Zeile */}
        {(bot.strat.risk_pct != null || bot.strat.max_contracts != null) && (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 6,
            padding: "8px 10px",
            background: theme.tint,
            borderRadius: 8,
            marginBottom: 10,
            fontSize: 10,
            fontFamily: fonts.mono,
          }}>
            <div>
              <div style={{ fontSize: 8, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: "0.5px" }}>Risk</div>
              <div style={{ fontSize: 12, fontWeight: 800, color: theme.text }}>{bot.strat.risk_pct != null ? `${bot.strat.risk_pct}%` : "—"}</div>
            </div>
            <div>
              <div style={{ fontSize: 8, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: "0.5px" }}>Max Cap</div>
              <div style={{ fontSize: 12, fontWeight: 800, color: theme.text }}>{bot.strat.max_contracts != null ? `${bot.strat.max_contracts}×` : "—"}</div>
            </div>
            <div>
              <div style={{ fontSize: 8, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: "0.5px" }}>Capital</div>
              <div style={{ fontSize: 12, fontWeight: 800, color: theme.text }}>{bot.strat.initial_capital != null ? `$${(bot.strat.initial_capital/1000).toFixed(0)}k` : "—"}</div>
            </div>
          </div>
        )}
        
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, paddingTop: 12, borderTop: `1px solid ${theme.divider}` }}>
          <Metric label="Win Rate" value={`${bot.wr}%`} color={Number(bot.wr) > 55 ? theme.greenText : theme.text3} small />
          <Metric label="Trades" value={String(bot.trades)} small />
          <Metric label="Today" value={`${bot.todayTrades} fills`} small />
          <Metric
            label="Position"
            value={bot.pos
              ? `${bot.pos.direction || bot.pos.side} ${currentQty}${maxContracts > 0 ? `/${maxContracts}` : ""}`
              : "Flat"}
            color={bot.pos ? (capUsage > 80 ? theme.amberText : theme.blueText) : theme.text3}
            small
          />
        </div>
      </div>
    </Card>
  );
  };

  return <>
    {/* Alerts */}
    {(hasMismatch || warningBots.length > 0 || paperWarningBots.length > 0) && <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
      {hasMismatch && <div style={{ padding: "10px 16px", borderRadius: 10, background: theme.redLight, border: "1px solid rgba(220,38,38,0.08)", display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 14, color: theme.redText, fontWeight: 700 }}>Position Mismatch</span>
        <span style={{ flex: 1 }} />
        <Button danger onClick={() => fetch(`${API_BASE}/reconciliation/check`, { method: "POST" })}>Force Sync</Button>
      </div>}
      {warningBots.map(bot => <div key={bot.name} style={{ padding: "10px 16px", borderRadius: 10, background: theme.amberLight, border: "1px solid rgba(217,119,6,0.1)", fontSize: 12, color: theme.amberText }}><strong>{String(bot.name)}</strong> {"\u2014"} Live but adapter disconnected</div>)}
      {paperWarningBots.map(bot => <div key={bot.name} style={{ padding: "10px 16px", borderRadius: 10, background: theme.amberLight, border: "1px solid rgba(217,119,6,0.1)", fontSize: 12, color: theme.amberText }}><strong>{String(bot.name)}</strong> {"\u2014"} Paper but adapter disconnected</div>)}
    </div>}

    {/* Headline */}
    <div className="sl-grid-6" style={{ display: "grid", gridTemplateColumns: "2fr 1.5fr 1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
      <Card style={{ padding: "20px 22px", borderLeft: `3px solid ${theme.greenBright}` }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: "1.2px", marginBottom: 6 }}>Portfolio P&L</div>
        <div style={{ fontSize: 32, fontWeight: 900, fontFamily: fonts.mono, color: pnlColor(totalPnl), letterSpacing: "-1.5px" }}>{formatPnl(totalPnl)}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}><Pill bg={theme.greenLight} color={theme.green}>{String(exits.length)} trades</Pill><span style={{ fontSize: 11, color: theme.text3 }}>{liveBots.length} live {"\u00B7"} {paperBots.length} paper {"\u00B7"} {testBots.length} test</span></div>
      </Card>
      <Card style={{ padding: "20px 22px", borderLeft: `3px solid ${todayPnl >= 0 ? theme.greenBright : theme.red}` }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: "1.2px", marginBottom: 6 }}>Today</div>
        <div style={{ fontSize: 26, fontWeight: 900, fontFamily: fonts.mono, color: pnlColor(todayPnl), letterSpacing: "-1px" }}>{formatPnl(todayPnl)}</div>
        <div style={{ fontSize: 11, color: theme.text3, marginTop: 6 }}>{String(todayTrades.length)} fills</div>
      </Card>
      {[["Win Rate", `${winRate}%`], ["Positions", String(Object.keys(positions).length)], ["Live Bots", `${liveBots.length}/${Object.keys(strategies).length}`]].map(([l, v], i) => (
        <Card key={i} style={{ padding: "20px 18px", textAlign: "center", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 6 }}>{l}</div>
          <div style={{ fontSize: 24, fontWeight: 900, fontFamily: fonts.mono, color: theme.text }}>{v}</div>
        </Card>
      ))}
    </div>

    {/* v4-FEAT-E: Analyze Tile — latest WF + Optimizer summary with deep-link */}
    {analyzeRuns?.runs?.length > 0 && (() => {
      const latestWf = analyzeRuns.runs.find(r => r.run_type === "walkforward");
      const latestOpt = analyzeRuns.runs.find(r => r.run_type === "optimizer");
      if (!latestWf && !latestOpt) return null;

      const verdictColor = (v) => v === "ROBUST" ? theme.greenText : v === "FRAGILE" ? theme.amberText : theme.redText;
      const verdictBg = (v) => v === "ROBUST" ? theme.greenLight : v === "FRAGILE" ? theme.amberLight : theme.redLight;

      return <div style={{ marginBottom: 20 }}>
        <SectionTitle>Strategie-Analyse</SectionTitle>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 12 }}>

          {/* Latest WF Tile */}
          {latestWf && (
            <Card className="sl-card" style={{ cursor: "pointer" }} onClick={() => nav && nav("strategy-lab")}>
              <div style={{ height: 3, background: `linear-gradient(90deg, ${verdictColor(latestWf.verdict)}, transparent)` }}/>
              <div style={{ padding: "14px 18px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                      <Pill bg={theme.amberLight} color={theme.amberText}>Walk-Forward</Pill>
                      <span style={{ fontSize: 14, fontWeight: 800, color: theme.text }}>{latestWf.instrument}</span>
                      <span style={{ fontSize: 10, color: theme.text4, fontFamily: fonts.mono }}>#{latestWf.run_id}</span>
                    </div>
                    <div style={{ fontSize: 10, color: theme.text3, fontFamily: fonts.mono }}>
                      {latestWf.windows}/{latestWf.total_windows} Windows · {new Date(latestWf.mtime * 1000).toLocaleString("de-DE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                  <Pill bg={verdictBg(latestWf.verdict)} color={verdictColor(latestWf.verdict)}>{latestWf.verdict || "?"}</Pill>
                </div>
                <div style={{ fontSize: 22, fontWeight: 900, fontFamily: fonts.mono, color: (latestWf.total_oos_pnl || 0) >= 0 ? theme.greenText : theme.redText, letterSpacing: "-1px", marginBottom: 10 }}>
                  {(latestWf.total_oos_pnl || 0) >= 0 ? "+" : "-"}${Math.abs(latestWf.total_oos_pnl || 0).toLocaleString()}
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", paddingTop: 10, borderTop: `1px solid ${theme.divider}` }}>
                  <span style={{ fontSize: 10, color: theme.text3 }}>Letzter Walk-Forward · OOS-PnL</span>
                  <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 700, color: theme.blueText }}>🔬 Analysieren →</span>
                </div>
              </div>
            </Card>
          )}

          {/* Latest Optimizer Tile */}
          {latestOpt && (
            <Card className="sl-card" style={{ cursor: "pointer" }} onClick={() => nav && nav("strategy-lab")}>
              <div style={{ height: 3, background: `linear-gradient(90deg, ${theme.purple}, transparent)` }}/>
              <div style={{ padding: "14px 18px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                      <Pill bg={theme.purpleLight} color={theme.purpleText}>Optimizer</Pill>
                      <span style={{ fontSize: 14, fontWeight: 800, color: theme.text }}>{latestOpt.instrument}</span>
                      <span style={{ fontSize: 10, color: theme.text4, fontFamily: fonts.mono }}>#{latestOpt.run_id}</span>
                    </div>
                    <div style={{ fontSize: 10, color: theme.text3, fontFamily: fonts.mono }}>
                      {latestOpt.n_trials} Trials · {new Date(latestOpt.mtime * 1000).toLocaleString("de-DE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                  <Pill bg={latestOpt.best_grade === "A" ? theme.greenLight : theme.amberLight} color={latestOpt.best_grade === "A" ? theme.greenText : theme.amberText}>
                    Grade {latestOpt.best_grade || "?"}
                  </Pill>
                </div>
                <div style={{ fontSize: 22, fontWeight: 900, fontFamily: fonts.mono, color: (latestOpt.best_pnl || 0) >= 0 ? theme.greenText : theme.redText, letterSpacing: "-1px", marginBottom: 10 }}>
                  {(latestOpt.best_pnl || 0) >= 0 ? "+" : "-"}${Math.abs(latestOpt.best_pnl || 0).toLocaleString()}
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", paddingTop: 10, borderTop: `1px solid ${theme.divider}` }}>
                  <span style={{ fontSize: 10, color: theme.text3 }}>Best-Score {(latestOpt.best_score || 0).toFixed(3)}</span>
                  <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 700, color: theme.blueText }}>🔬 Analysieren →</span>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>;
    })()}

    {/* MarketData Streams (Sprint 1.4) */}
    <MarketDataWidget />

    {/* Live Bots */}
    {liveBots.length > 0 && <div style={{ marginBottom: 20 }}>
      <SectionTitle>Live Trading</SectionTitle>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(liveBots.length, 3)}, 1fr)`, gap: 12 }}>
        {liveBots.map(bot => <BotCard key={bot.name} bot={bot} />)}
      </div>
    </div>}

    {/* Paper Bots (Sprint 1.4) */}
    {paperBots.length > 0 && <div style={{ marginBottom: 20 }}>
      <SectionTitle>Paper Trading</SectionTitle>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(paperBots.length, 3)}, 1fr)`, gap: 12 }}>
        {paperBots.map(bot => <BotCard key={bot.name} bot={bot} />)}
      </div>
    </div>}

    {/* Test Bots */}
    {testBots.length > 0 && <div style={{ marginBottom: 20 }}>
      <button onClick={() => setShowTest(!showTest)} style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, color: theme.text3, textTransform: "uppercase", letterSpacing: "1.2px", padding: "0 0 10px" }}>
        <span style={{ transform: showTest ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s", fontSize: 10 }}>{"\u25B6"}</span>
        Test Strategies ({testBots.length})
      </button>
      {showTest && <Card>{testBots.map(bot => {
        // v8.0: Profile-Color-Map
        const profileColors = {
          KONSERVATIV: { bg: "rgba(34,197,94,0.10)", text: "#16a34a", border: "rgba(34,197,94,0.3)" },
          MODERAT:     { bg: "rgba(59,130,246,0.10)", text: "#3b82f6", border: "rgba(59,130,246,0.3)" },
          AGGRESSIV:   { bg: "rgba(239,68,68,0.10)",  text: "#ef4444", border: "rgba(239,68,68,0.3)" },
        };
        const profile = bot.strat.profile;
        const pColor = profile && profileColors[profile];
        const riskPct = bot.strat.risk_pct;
        const maxCap = bot.strat.max_contracts;
        const initCap = bot.strat.initial_capital;
        
        return (
        <div key={bot.name} className="sl-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", borderBottom: `1px solid ${theme.divider}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
            <span style={{ fontFamily: fonts.mono, fontWeight: 700, fontSize: 12, color: theme.text, width: 48 }}>{String(bot.strat.symbol || bot.name.split("_")[0])}</span>
            <span style={{ fontSize: 12, color: theme.text3, marginRight: 8 }}>{String(bot.name)}</span>
            {profile && pColor && (
              <span style={{
                padding: "2px 8px",
                borderRadius: 6,
                fontSize: 9,
                fontWeight: 800,
                letterSpacing: "0.5px",
                background: pColor.bg,
                color: pColor.text,
                border: `1px solid ${pColor.border}`,
                fontFamily: fonts.mono,
              }}>{profile}</span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            {riskPct != null && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                <span style={{ fontSize: 8, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: "0.5px" }}>Risk</span>
                <span style={{ fontSize: 11, fontFamily: fonts.mono, fontWeight: 700, color: theme.text }}>{riskPct}%</span>
              </div>
            )}
            {maxCap != null && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                <span style={{ fontSize: 8, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: "0.5px" }}>Cap</span>
                <span style={{ fontSize: 11, fontFamily: fonts.mono, fontWeight: 700, color: theme.text }}>{maxCap}{"\u00D7"}</span>
              </div>
            )}
            {initCap != null && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                <span style={{ fontSize: 8, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: "0.5px" }}>Cap.</span>
                <span style={{ fontSize: 11, fontFamily: fonts.mono, fontWeight: 700, color: theme.text }}>${(initCap/1000).toFixed(0)}k</span>
              </div>
            )}
            <span style={{ fontSize: 11, fontFamily: fonts.mono, color: theme.text3, minWidth: 30, textAlign: "right" }}>{String(bot.strat.timeframe || "M5")}</span>
            <span style={{ fontSize: 12, fontFamily: fonts.mono, fontWeight: 700, color: pnlColor(bot.pnl), minWidth: 60, textAlign: "right" }}>{formatPnl(bot.pnl)}</span>
            <span style={{ fontSize: 11, color: theme.text4, minWidth: 40, textAlign: "right" }}>{String(bot.trades)} tr</span>
          </div>
        </div>
        );
      })}</Card>}
    </div>}

    {/* Equity + Charts */}
    <div className="sl-grid-4" style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 14, marginBottom: 20 }}>
      <Card>
        <CardHeader title="Portfolio Equity" sub="Cumulative P&L" />
        <div style={{ padding: "12px 20px 16px" }}><EquityChart data={eqData} /></div>
      </Card>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <Card style={{ flex: 1 }}>
          <CardHeader title="P&L by Hour" sub="Today" />
          <div style={{ padding: "8px 14px 12px" }}><BarChart data={hourlyData} labels={hourlyLabels} /></div>
        </Card>
        <Card style={{ flex: 1, padding: "14px 18px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 700 }}>Strategy</span>
            {(() => {
              // v8.0: Zeige Werte des ersten LIVE-Bots, sonst des ersten test-Bots
              const refBot = liveBots[0] || testBots[0];
              const profile = refBot?.strat?.profile;
              const profileColors = {
                KONSERVATIV: { bg: "rgba(34,197,94,0.10)", text: "#16a34a", border: "rgba(34,197,94,0.3)" },
                MODERAT:     { bg: "rgba(59,130,246,0.10)", text: "#3b82f6", border: "rgba(59,130,246,0.3)" },
                AGGRESSIV:   { bg: "rgba(239,68,68,0.10)",  text: "#ef4444", border: "rgba(239,68,68,0.3)" },
              };
              const pColor = profile && profileColors[profile];
              return profile && pColor ? (
                <span style={{
                  padding: "2px 7px",
                  borderRadius: 5,
                  fontSize: 8,
                  fontWeight: 800,
                  letterSpacing: "0.5px",
                  background: pColor.bg,
                  color: pColor.text,
                  border: `1px solid ${pColor.border}`,
                  fontFamily: fonts.mono,
                }}>{profile}</span>
              ) : null;
            })()}
          </div>
          {(() => {
            // v8.0: dynamische Werte aus dem (ersten) Bot
            const refBot = liveBots[0] || testBots[0];
            const s = refBot?.strat || {};
            const items = [
              ["Risk/Trade", s.risk_pct != null ? `${s.risk_pct}%` : "\u2014"],
              ["Max Cap",    s.max_contracts != null ? `${s.max_contracts}\u00D7` : "\u2014"],
              ["Capital",    s.initial_capital != null ? `$${(s.initial_capital/1000).toFixed(0)}k` : "\u2014"],
              ["Min SL",     s.min_sl_points != null ? `${s.min_sl_points}p` : "\u2014"],
              ["Commission", s.commission_per_contract != null ? `$${s.commission_per_contract}/c` : "\u2014"],
            ];
            return items.map(([k, v], i, a) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: i < a.length - 1 ? `1px solid ${theme.divider}` : "none" }}>
                <span style={{ fontSize: 11, color: theme.text3 }}>{k}</span>
                <span style={{ fontSize: 11, fontWeight: 700, fontFamily: fonts.mono }}>{v}</span>
              </div>
            ));
          })()}
        </Card>
      </div>
    </div>

    {/* Trades + System */}
    <div className="sl-grid-4" style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 14 }}>
      <Card>
        <CardHeader title="Recent Trades" sub={`Last ${recentTrades.length}`} right={<Button onClick={() => nav("journal")}>Journal {"\u2192"}</Button>} />
        <div style={{ overflow: "auto", maxHeight: 380 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={{ borderBottom: `1px solid ${theme.divider}` }}>
              {["Time", "Strategy", "", "Qty", "Price", "P&L"].map((h, i) => (
                <th key={i} style={{ padding: "8px 14px", fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: "0.8px", textAlign: i === 5 ? "right" : "left" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>{recentTrades.map((t, i) => {
              const w = (t.pnl || 0) >= 0;
              const time = t.timestamp ? new Date(t.timestamp).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : "--";
              const isNew = t.timestamp && (Date.now() - new Date(t.timestamp).getTime()) < 120000;
              return (
                <tr key={i} className="sl-row" style={{ borderBottom: `1px solid ${theme.divider}`, background: isNew ? "rgba(22,163,74,0.03)" : undefined }}>
                  <td style={{ padding: "9px 14px", fontSize: 12, fontFamily: fonts.mono, color: theme.text3 }}>
                    {time}{isNew && <span style={{ marginLeft: 6, fontSize: 8, fontWeight: 800, color: theme.greenText, background: theme.greenLight, padding: "1px 5px", borderRadius: 4 }}>NEW</span>}
                  </td>
                  <td style={{ padding: "9px 14px", fontSize: 12, fontWeight: 600, fontFamily: fonts.mono }}>{String(t.strategy || "\u2014")}</td>
                  <td style={{ padding: "9px 8px" }}><Pill bg={t.action === "entry" ? theme.blueLight : w ? theme.greenLight : theme.redLight} color={t.action === "entry" ? theme.blueText : w ? theme.greenText : theme.redText}>{String(t.action || "\u2014")}</Pill></td>
                  <td style={{ padding: "9px 14px", fontSize: 12, fontFamily: fonts.mono, color: theme.text2 }}>{String(t.quantity || "\u2014")}</td>
                  <td style={{ padding: "9px 14px", fontSize: 12, fontFamily: fonts.mono, color: theme.text2 }}>{t.price ? Number(t.price).toFixed(2) : "\u2014"}</td>
                  <td style={{ padding: "9px 14px", fontSize: 12, fontWeight: 700, fontFamily: fonts.mono, color: t.action === "exit" ? pnlColor(t.pnl) : theme.text3, textAlign: "right" }}>{t.action === "exit" ? formatPnl(t.pnl) : "\u2014"}</td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      </Card>
      <Card>
        <CardHeader title="System" right={
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: ibConnected ? theme.green : theme.red, animation: ibConnected ? "liveDot 2.5s infinite" : "none" }} />
            <span style={{ fontSize: 10, fontWeight: 700, color: ibConnected ? theme.greenText : theme.redText }}>{ibConnected ? "Connected" : "Offline"}</span>
          </div>
        } />
        <div style={{ padding: "12px 18px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Metric label="IB Gateway" value={ibConnected ? "Connected" : "Down"} color={ibConnected ? theme.greenText : theme.redText} />
          <Metric label="Strategies" value={`${liveBots.length} / ${Object.keys(strategies).length}`} />
          <Metric label="Uptime" value={String(status.uptime_human || status.uptime || "\u2014")} />
          <Metric label="Account" value={String(acctList[0]?.account_id || "Paper")} />
        </div>
        <div style={{ padding: "8px 18px 12px", borderTop: `1px solid ${theme.divider}` }}>
          <span style={{ fontSize: 10, fontFamily: fonts.mono, color: theme.text4 }}>{String(status.host || "178.104.155.90")}</span>
        </div>
      </Card>
    </div>
  </>;
}
